<template>
  <div class="hiaTable level2">
    <table ref="dxGrid" id="dxGrid" class="dx-table">
      <thead>
        <tr v-if="worksheet.dxs.length > 0" class="code-type-header-row">
          <th colspan="4" class="codeTypeHeader groupBlock groupDividerEnd">{{ worksheet.dxs[0].preDxCodeDesc }}</th>
          <th colspan="4" class="codeTypeHeader groupBlock groupDividerStart groupDividerEnd">{{ worksheet.dxs[0].codeDesc }}</th>
          <th colspan="4" class="codeTypeHeader groupBlock groupDividerStart groupDividerEnd">Recommended Dx Coding</th>
          <th colspan="2" class="groupDividerStart">&nbsp;</th>
        </tr>
        <tr>
          <th class="top-header">Dx</th>
          <th class="top-header">POA</th>
          <th class="top-header">Desc.</th>
          <th class="top-header groupDividerEnd">HCC</th>
          <th class="top-header groupDividerStart">Dx</th>
          <th class="top-header">POA</th>
          <th class="top-header">Desc.</th>
          <th class="top-header groupDividerEnd">HCC</th>
          <th class="top-header groupDividerStart">Rec. Dx</th>
          <th class="top-header">POA</th>
          <th class="top-header">Desc.</th>
          <th class="top-header groupDividerEnd">HCC</th>
          <th class="top-header groupDividerStart">Reason For Change</th>
          <th class="top-header">&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="dx in worksheet.dxs"
          :key="dx.dxId"
          class="dx-row"
          :class="{ highlight: isDxRowChange(dx) }"
        >
          <!-- Pre Dx -->
          <td :class="{ 'red-font': isPreDxRedFont(dx) }">
            <div>{{ dx.preDx }}</div>
          </td>
          <td :class="{ 'red-font': isPreDxRedFont(dx) }">
            <div style="text-align: center;">&nbsp;</div>
          </td>
          <td :class="{ 'red-font': isPreDxRedFont(dx) }">
            <div style="text-transform: lowercase;" :title="dx.preDxDesc">{{ dx.preDxDesc }}</div>
          </td>
          <td class="groupDividerEnd" :class="{ 'red-font': isPreDxRedFont(dx) }" style="font-size: 13px;">
            <div>{{ dx.preDxHccs }}</div>
            <div>{{ dx.preDxHccsV28 }}</div>
          </td>

          <!-- Original (middle) Dx -->
          <td
            class="groupDividerStart"
            :class="{ 'red-font': isOriginalRedFont(dx) }"
            :style="dx.preDxDeleted ? 'text-decoration: line-through' : ''"
          >
            <div>{{ getOriginalDxCode(dx) }}</div>
          </td>
          <td :class="{ 'red-font': isOriginalRedFont(dx) }">
            <div style="text-align: center;">{{ dx.originalPOA }}</div>
          </td>
          <td :class="{ 'red-font': isOriginalRedFont(dx) }">
            <div style="text-transform: lowercase;" :title="getOriginalDxDescription(dx)">
              {{ getOriginalDxDescription(dx) }}
              <span
                v-if="dx.originalGrouperFlags && !dx.preDxDeleted"
                :title="getGrouperflagsTitle(dx.originalGrouperFlags)"
                class="grouperFlags"
              >({{ dx.originalGrouperFlags }})</span>
            </div>
          </td>
          <td class="groupDividerEnd" :class="{ 'red-font': isOriginalRedFont(dx) }" style="font-size: 13px;">
            <div>{{ dx.originalHccs }}</div>
            <div>{{ dx.originalHccsV28 }}</div>
          </td>

          <!-- Recommended Dx -->
          <td
            class="groupDividerStart"
            :class="{ 'red-font': isRecommendedRedFont(dx) }"
            :style="dx.originalDeleted ? 'text-decoration: line-through' : ''"
          >
            <div>{{ getRecommendedDxCode(dx) }}</div>
          </td>
          <td :class="{ 'red-font': isRecommendedRedFont(dx) }">
            <div style="text-align: center;">{{ dx.recommendedPOA }}</div>
          </td>
          <td :class="{ 'red-font': isRecommendedRedFont(dx) }">
            <div style="text-transform: lowercase;" :title="getDxRecommendedDescription(dx)">
              {{ getDxRecommendedDescription(dx) }}
              <span
                v-if="showRecommendedGrouperFlags(dx)"
                :title="getGrouperflagsTitle(dx.recommendedGrouperFlags)"
                class="grouperFlags"
              >({{ dx.recommendedGrouperFlags }})</span>
            </div>
          </td>
          <td class="groupDividerEnd" :class="{ 'red-font': isRecommendedRedFont(dx) }" style="font-size: 13px;">
            <div>{{ dx.recommendedHccs }}</div>
            <div>{{ dx.recommendedHccsV28 }}</div>
          </td>

          <td class="groupDividerStart" :class="{ 'red-font': isRecommendedRedFont(dx) }">{{ dx.reasonForChange }}</td>
          <td>
            <el-button
              :disabled="coderResponsesReadOnly()"
              style="margin-right: 5px;"
              size="mini"
              v-if="shouldShowRespond(dx)"
              type="text"
              @click="addResponse(dx)"
            >Respond</el-button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
  import HiaAuthorization from '@/mixins/hiaAuthorization'
  import coderReviewStatusHelper from '@/mixins/coderReviewStatusHelper'
  import { mapMutations } from 'vuex'

  export default {
    name: 'coderProDxsPivoted',
    mixins: [HiaAuthorization, coderReviewStatusHelper],
    props: ['worksheet'],
    methods: {
      ...mapMutations('coder/', ['SET_RESPONSE_ROW']),
      getGrouperflagsTitle(flagList) {
        if (!flagList) {
          return
        }

        const flagMap = {
          AD: 'Affects DRG',
          CC: 'Complication/Comorbidity',
          HC: 'Hospital-Acquired Condition',
          MC: 'Complication/Comorbidity',
          R: 'Affects Risk of Mortality',
          S: 'Affects Severity of Illness',
          AP: 'Affects Patient Safety Indicators',
          EP: 'Excludes Patient Safety Indicators',
          AQ: 'Affects Quality Measures',
          EQ: 'Excludes Quality Measures',
          OR: 'Operative Procedure',
          NOR: 'Non-Operative Procedure'
        }

        const flags = flagList.split(',')
        const title = []
        flags.forEach(x => {
          title.push(`${x}: ${flagMap[x]}`)
        })

        return title.join('\n')
      },
      addResponse(row) {
        this.SET_RESPONSE_ROW(row)
        this.$nextTick(() => {
          const editor = document.getElementById('newCommentExistingRebuttal')
          if (editor) {
            editor.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
          }
        })
      },
      shouldShowRespond(dx) {
        if (dx.recommendedDx || dx.originalDeleted || dx.recommendedPOA) {
          return true
        }
        if (dx.preDxDeleted || dx.newPrimary || dx.preDxNewPrimary) {
          return true
        }
        if (dx.preDx && dx.originalDx && dx.preDx !== dx.originalDx) {
          return true
        }
        return false
      },
      isDxRowChange(dx) {
        if (dx.recommendedDx || dx.originalDeleted || dx.preDxDeleted) {
          return true
        }
        if (dx.recommendedPOA || dx.newPrimary || dx.preDxNewPrimary) {
          return true
        }
        if (dx.preDx && dx.originalDx && dx.preDx !== dx.originalDx) {
          return true
        }
        return false
      },
      isPreDxRedFont(dx) {
        if (dx.preDxDeleted || (dx.originalDeleted && dx.recommendedDx !== dx.preDx)) {
          return true
        }
        return false
      },
      isOriginalRedFont(dx) {
        return dx.originalDeleted || dx.preDxDeleted
      },
      isRecommendedRedFont(dx) {
        return dx.originalDeleted || dx.preDxDeleted
      },
      getOriginalDxCode(dx) {
        if (dx.preDxDeleted) {
          return dx.preDx
        }
        return dx.originalDx
      },
      getOriginalDxDescription(dx) {
        if (dx.preDxDeleted) {
          return 'Delete Code'
        }
        return dx.originalDescription
      },
      getRecommendedDxCode(dx) {
        if (dx.originalDeleted) {
          return dx.originalDx || dx.preDx || 'DEL'
        }
        return dx.recommendedDx
      },
      getDxRecommendedDescription(dx) {
        if (dx.originalDeleted) {
          if (dx.recommendedDesc) {
            return `Delete Diagnosis - ${dx.recommendedDesc}`
          }
          return 'Delete Code'
        }

        if (dx.recommendedDesc && dx.rowNumber === 1 && this.worksheet.dxs.some(x => x.newPrimary)) {
          if (dx.recommendedDesc) {
            return `Resequence Principal Diagnosis - ${dx.recommendedDesc}`
          }
          return 'Resequence Principal Diagnosis'
        }

        return dx.recommendedDesc
      },
      showRecommendedGrouperFlags(dx) {
        return (
          (dx.recommendedDx && dx.recommendedGrouperFlags) ||
          (dx.recommendedGrouperFlags && dx.recommendedGrouperFlags !== dx.originalGrouperFlags)
        )
      }
    }
  }
</script>

<style scoped>
  .grouperFlags {
    font-style: italic;
    text-transform: uppercase;
  }

  .red-font {
    color: red;
  }

  .dx-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 0;
  }

  .hiaTable.level2 {
    overflow-x: auto;
  }

  .dx-table .top-header {
    padding: 8px 8px 2px 8px;
    text-align: left;
    vertical-align: middle;
    background-color: #f5f7fa;
    font-weight: 600;
    border-bottom: 2px solid #e2e8f0;
  }

  .dx-table td {
    padding: 8px;
    text-align: left;
    vertical-align: top;
    font-size: 13px;
  }

  .dx-table tbody tr:nth-child(even) {
    background-color: #fafafa;
  }

  .dx-table tbody tr {
    border-top: 1px solid #e2e8f0;
  }

  .groupDividerStart {
    border-left: 1px solid #c0c4cc !important;
  }

  .groupDividerEnd {
    border-right: 1px solid #c0c4cc !important;
  }

  .codeTypeHeader {
    text-align: left;
    padding: 4px 8px 4px 8px;
    border-left: 8px solid #3378aa7c;
    color: #2c3e50;
    font-style: italic;
    font-weight: normal;
    background-color: #f5f7fa;
  }

  .codeTypeHeader.groupBlock {
    border-right: 1px solid #c0c4cc;
  }

  .codeTypeHeader.groupDividerStart {
    border-left: 1px solid #c0c4cc;
  }

  .code-type-header-row th {
    border-bottom: 1px solid #ebeef5;
  }

  >>> .highlight {
    font-weight: bold;
    border-left: 3px solid #f56c6c;
  }
</style>
